'use client';

import { format, parseISO } from 'date-fns';
import { LinkifiedText } from '@/app/(main)/calendar/components/LinkifiedText';
import { CALENDAR_EVENT_TYPES } from '@/app/(main)/calendar/consts/calendar-event-types';
import {
  getOutlookEventDisplayNotes,
  parseOutlookEventDescription,
} from '@/app/(main)/calendar/utils/parseOutlookEventDescription';
import { Modal } from '@/app/components/Modal';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import type { CalendarEvent } from '@/types/calendarTypes';
import ClockIcon from '/public/icons/clock.svg';
import LocationIcon from '/public/icons/location-pin.svg';

function formatEventWhen(event: CalendarEvent): string {
  const start = parseISO(event.start);
  if (Number.isNaN(start.getTime())) return '';
  const dateLabel = format(start, 'EEEE, MMMM d, yyyy');
  if (event.allDay) {
    return `${dateLabel} · ALL DAY`;
  }
  const end = event.end ? parseISO(event.end) : null;
  const time =
    end && !Number.isNaN(end.getTime())
      ? `${format(start, 'h:mm a')}–${format(end, 'h:mm a')}`
      : format(start, 'h:mm a');
  return `${dateLabel} · ${time}`;
}

function mapsHref(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

type OutlookEventLinkListProps = {
  links: { label: string; url: string }[];
};

function OutlookEventLinkList({ links }: OutlookEventLinkListProps) {
  if (links.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 pl-8">
      {links.map((link) => (
        <a
          key={`${link.label}-${link.url}`}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-body-small text-blue-300 hover:underline [overflow-wrap:anywhere]"
        >
          {link.label}
        </a>
      ))}
    </div>
  );
}

type OutlookEventDetailsModalProps = {
  event: CalendarEvent | null;
  isOpen: boolean;
  onClose: () => void;
};

export function OutlookEventDetailsModal({ event, isOpen, onClose }: OutlookEventDetailsModalProps) {
  if (!event) return null;

  const isVirtual = event.type === CALENDAR_EVENT_TYPES.OUTLOOK_VIRTUAL;
  const parsed = parseOutlookEventDescription(event.description, event.externalUrl);
  const displayNotes = getOutlookEventDisplayNotes(event.description, event.address);
  const hasMeetingDetails = Boolean(parsed.meetingId || parsed.passcode);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className="flex w-[min(100vw-32px,480px)] max-h-[min(800px,90vh)] flex-col overflow-hidden">
        <div className="flex shrink-0 flex-col md:px-9 px-4 pt-11.5">
          <p className="mb-2 pr-8 font-inter md:text-xl text-base font-medium text-black [overflow-wrap:anywhere]">
            {event.title}
          </p>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto md:px-9 px-4">
          <div className="mb-2 flex items-start gap-2">
            <ClockIcon className="mt-0.5 h-6 w-6 shrink-0 stroke-black" />
            <p className="md:text-body-regular text-body-small text-blue-300">{formatEventWhen(event)}</p>
          </div>

          {displayNotes ? (
            <p className="md:text-body-regular text-body-small whitespace-pre-wrap pl-8 text-grey-700 [overflow-wrap:anywhere]">
              <LinkifiedText text={displayNotes} />
            </p>
          ) : null}

          {isVirtual && hasMeetingDetails ? (
            <div className="mt-4 space-y-1 rounded-lg border border-grey-200 bg-grey-50 px-4 py-3 pl-8">
              {parsed.meetingId ? (
                <p className="text-body-small text-grey-700">
                  <span className="font-medium text-grey-800">Meeting ID:</span> {parsed.meetingId}
                </p>
              ) : null}
              {parsed.passcode ? (
                <p className="text-body-small text-grey-700">
                  <span className="font-medium text-grey-800">Passcode:</span> {parsed.passcode}
                </p>
              ) : null}
            </div>
          ) : null}

          {isVirtual ? <OutlookEventLinkList links={parsed.links} /> : null}

          {!isVirtual && event.address ? (
            <p className="mt-3 flex items-start gap-2 md:text-body-regular text-body-small">
              <LocationIcon className="mt-0.5 h-6 w-6 shrink-0 stroke-black" />
              <a
                href={mapsHref(event.address)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-300 hover:underline [overflow-wrap:anywhere]"
              >
                {event.address}
              </a>
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col gap-3 md:px-9 px-4 md:pb-11.5 pb-6 md:pt-9 pt-6">
          {isVirtual && parsed.meetingUrl ? (
            <div className="flex gap-3">
              <BaseButton intent="secondary" className="flex-1" onClick={onClose}>
                Cancel
              </BaseButton>
              <BaseButton
                intent="primary"
                className="flex-1"
                onClick={() => {
                  window.open(parsed.meetingUrl ?? undefined, '_blank', 'noopener,noreferrer');
                  onClose();
                }}
              >
                Join meeting
              </BaseButton>
            </div>
          ) : (
            <BaseButton intent="primary" className="w-full" onClick={onClose}>
              Close
            </BaseButton>
          )}
        </div>
      </div>
    </Modal>
  );
}
